import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import {
  ArrowUp,
  BadgeCheck,
  CircleAlert,
  ExternalLink,
  FileText,
  Globe,
  LoaderCircle,
  MessageCircleQuestion,
  Mic,
  MicOff,
  Share2,
  ShieldCheck,
  Volume2
} from 'lucide-react'
import type { LanguagePack } from '../i18n/packs'
import type { AskSuggestId } from '../i18n/packs/types'
import { languageInfo } from '../i18n/languages'
import { useVoiceInput } from '../hooks/useVoiceInput'
import type { useSpeech } from '../hooks/useSpeech'
import { AskFailure, askQuestion, documentTextForAsk, officialWebsites, speakableAnswer, type AskResult } from '../services/ask'
import { displayHost, LEGAL_SOURCES, sourcesFor } from '../shared/trusted-sources'
import { SCHEME_TYPES } from '../services/nearby'
import type { Language, OcrResult, StructuredDocument } from '../types/document'

type Speech = ReturnType<typeof useSpeech>

interface Turn {
  id: number
  question: string
  status: 'loading' | 'done' | 'error'
  result?: AskResult
  error?: AskFailure['reason']
}

/** Questions that fit this document, most urgent first. */
/**
 * A blank form to fill in: several lines that end in a label with an empty
 * space after it ("Name: ______", "पिता का नाम ........", "Mobile No. :").
 */
export function looksLikeBlankForm(ocr: OcrResult | null): boolean {
  if (!ocr) return false
  const blanks = ocr.lines.filter((l) => /(\p{L}[\p{L}\p{M}.\s/()]{1,40})\s*[:：-]?\s*(_{3,}|\.{4,}|…{2,})\s*$|\p{L}\s*[:：]\s*$/u.test(l.text.trim()))
  return blanks.length >= 4
}

export function suggestionsFor(doc: StructuredDocument, ocr: OcrResult | null = null): AskSuggestId[] {
  const ids: AskSuggestId[] = []
  if (looksLikeBlankForm(ocr)) ids.push('fillForm')
  if (doc.status?.value === 'rejected' || doc.status?.value === 'cancelled') ids.push('rejected')
  if (doc.deadline && doc.deadline.kind !== 'hearing') ids.push('deadline')
  if (doc.amounts.some((a) => a.kind === 'payable' || a.kind === 'fine') || ['electricity_bill', 'water_bill', 'property_tax', 'traffic_challan'].includes(doc.documentType)) ids.push('pay')
  if (doc.documentsRequired.length > 0) ids.push('papers')
  if (SCHEME_TYPES.includes(doc.documentType)) ids.push('schemes')
  ids.push('first')
  if (doc.contacts.length === 0) ids.push('contact')
  ids.push('meaning')
  return ids.slice(0, 4)
}

export function AskPanel({
  p,
  language,
  doc,
  ocr,
  speech,
  onShare,
  starters,
  title,
  hint
}: {
  p: LanguagePack
  language: Language
  /** Null on the legal help page: questions are answered only from official legal sources. */
  doc: StructuredDocument | null
  ocr: OcrResult | null
  /** Starter questions for the legal page (the document screen picks its own). */
  starters?: string[]
  title?: string
  hint?: string
  speech: Speech
  onShare: (text: string) => void
}) {
  const [turns, setTurns] = useState<Turn[]>([])
  const [input, setInput] = useState('')
  const [searchWeb, setSearchWeb] = useState(true)
  const [consented, setConsented] = useState(false)
  const [pending, setPending] = useState<string | null>(null)
  const [phase, setPhase] = useState<'doc' | 'web'>('doc')
  const nextId = useRef(1)
  const abortRef = useRef<AbortController | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const documentText = useMemo(() => (doc ? documentTextForAsk(doc, ocr) : ''), [doc, ocr])
  // Sites printed on the document first, then the official sites for its type
  const domains = useMemo(
    () =>
      doc
        ? [...new Set([...officialWebsites(doc).map(displayHost), ...sourcesFor(doc.documentType).domains])].slice(0, 3)
        : LEGAL_SOURCES.domains.slice(0, 4),
    [doc]
  )
  const busy = turns.some((t) => t.status === 'loading')
  const voice = useVoiceInput((text, final) => {
    setInput(text)
    if (final && text) inputRef.current?.focus()
  })

  useEffect(() => () => abortRef.current?.abort(), [])

  // "Checking official websites…" once the document alone has had a few seconds
  useEffect(() => {
    if (!busy) return
    setPhase('doc')
    if (!searchWeb) return
    const timer = setTimeout(() => setPhase('web'), 4500)
    return () => clearTimeout(timer)
  }, [busy, searchWeb])

  function scrollToEnd() {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }))
  }

  function send(question: string) {
    const q = question.trim()
    if (!q || busy) return
    if (consented) void run(q)
    else setPending(q)
  }

  // Questions sent from elsewhere on the result screen (e.g. "Find schemes for me")
  const sendRef = useRef(send)
  sendRef.current = send
  useEffect(() => {
    const onAsk = (event: Event) => sendRef.current(String((event as CustomEvent<string>).detail ?? ''))
    window.addEventListener('samjho-ask', onAsk)
    return () => window.removeEventListener('samjho-ask', onAsk)
  }, [])

  function acceptConsent() {
    setConsented(true)
    if (pending) void run(pending)
    setPending(null)
  }

  async function run(q: string) {
    voice.stop()
    const id = nextId.current++
    const history = turns
      .filter((t) => t.status === 'done' && t.result && !t.result.notFound)
      .map((t) => ({ q: t.question, a: t.result!.answers.map((a) => a.text).join('\n') }))
    setTurns((all) => [...all, { id, question: q, status: 'loading' }])
    setInput('')
    scrollToEnd()
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const result = await askQuestion({
        question: q,
        language,
        doc,
        documentText,
        history,
        searchWeb: doc ? searchWeb : true,
        topic: doc ? 'document' : 'legal',
        signal: controller.signal
      })
      setTurns((all) => all.map((t) => (t.id === id ? { ...t, status: 'done', result } : t)))
    } catch (error) {
      if (controller.signal.aborted) return
      const reason = error instanceof AskFailure ? error.reason : 'failed'
      setTurns((all) => all.map((t) => (t.id === id ? { ...t, status: 'error', error: reason } : t)))
    } finally {
      scrollToEnd()
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    send(input)
  }

  const last = turns[turns.length - 1]
  const followups = last?.status === 'done' ? last.result?.followups ?? [] : []
  const chips = turns.length === 0 ? (doc ? suggestionsFor(doc, ocr).map((id) => p.askSuggest[id]) : starters ?? []) : followups

  return (
    <section className="ask-panel" id="ask" aria-labelledby="ask-title">
      <header className="ask-header">
        <span className="ask-badge" aria-hidden="true">
          <MessageCircleQuestion size={24} />
        </span>
        <div>
          <h2 id="ask-title">{title ?? p.askTitle}</h2>
          <p>{hint ?? p.askHint}</p>
        </div>
      </header>

      {turns.length > 0 && (
        <ol className="ask-thread" aria-live="polite">
          {turns.map((turn) => (
            <li key={turn.id} className="ask-turn">
              <p className="ask-question" lang={languageInfo(language).bcp47}>{turn.question}</p>
              {turn.status === 'loading' && (
                <div className="ask-thinking" role="status">
                  <LoaderCircle className="spin" size={20} aria-hidden="true" />
                  <span>{phase === 'web' ? p.askThinkingWeb : p.askThinkingDoc}</span>
                  <span className="ask-dots" aria-hidden="true"><i /><i /><i /></span>
                </div>
              )}
              {turn.status === 'error' && (
                <div className="ask-error">
                  <CircleAlert size={18} aria-hidden="true" />
                  <span>{turn.error === 'busy' ? p.askBusy : p.askFailed}</span>
                  {turn.error !== 'unavailable' && (
                    <button className="inline-action" onClick={() => send(turn.question)} disabled={busy}>
                      {p.tryAgain}
                    </button>
                  )}
                </div>
              )}
              {turn.status === 'done' && turn.result && (
                <AnswerView p={p} language={language} question={turn.question} result={turn.result} speech={speech} onShare={onShare} />
              )}
            </li>
          ))}
        </ol>
      )}
      <div ref={endRef} />

      {pending !== null && (
        <div className="consent-box ask-consent">
          <p>{doc ? p.askConsent : p.legalAskConsent}</p>
          <p className="ask-domains">
            <ShieldCheck size={16} aria-hidden="true" />
            <span>{p.askOfficialOnly} {domains.join(' · ')}</span>
          </p>
          <div className="fallback-row">
            <button className="primary-button" onClick={acceptConsent}>{p.askConsentConfirm}</button>
            <button className="text-button" onClick={() => setPending(null)}>{p.cancel}</button>
          </div>
        </div>
      )}

      {chips.length > 0 && !busy && pending === null && (
        <div className="ask-chips">
          {turns.length > 0 && <span className="ask-chips-label">{p.askTryNext}</span>}
          {chips.map((chip) => (
            <button key={chip} className="ask-chip" onClick={() => send(chip)}>
              {chip}
            </button>
          ))}
        </div>
      )}

      <form className="ask-form" onSubmit={submit}>
        <textarea
          ref={inputRef}
          className="ask-input"
          rows={1}
          maxLength={500}
          value={input}
          placeholder={voice.listening ? p.askListening : p.askPlaceholder}
          aria-label={p.askPlaceholder}
          lang={languageInfo(language).bcp47}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(input)
            }
          }}
        />
        {voice.supported && (
          <button
            type="button"
            className={`ask-icon-button ${voice.listening ? 'listening' : ''}`}
            onClick={() => (voice.listening ? voice.stop() : voice.start(languageInfo(language).bcp47))}
            aria-label={voice.listening ? p.stop : p.askMic}
            aria-pressed={voice.listening}
          >
            {voice.listening ? <MicOff size={22} aria-hidden="true" /> : <Mic size={22} aria-hidden="true" />}
          </button>
        )}
        <button type="submit" className="ask-icon-button ask-send" disabled={!input.trim() || busy} aria-label={p.askSend}>
          <ArrowUp size={22} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </form>

      {doc && (
        <label className="ask-toggle">
          <input type="checkbox" checked={searchWeb} onChange={(e) => setSearchWeb(e.target.checked)} />
          <Globe size={16} aria-hidden="true" />
          <span>{p.askWebToggle}</span>
        </label>
      )}
      <p className="ask-note">{p.askAiNote}</p>
    </section>
  )
}

function AnswerView({
  p,
  language,
  question,
  result,
  speech,
  onShare
}: {
  p: LanguagePack
  language: Language
  question: string
  result: AskResult
  speech: Speech
  onShare: (text: string) => void
}) {
  if (result.notFound) {
    return (
      <div className="ask-answer ask-none">
        <p>{p.askNotFound}</p>
      </div>
    )
  }

  const spoken = result.answers.map((a) => speakableAnswer(a.text))
  const shareText = [
    `❓ ${question}`,
    ...result.answers.flatMap((a) => [
      `${a.basis === 'document' ? '📄' : '🌐'} ${a.text}`,
      ...a.sources.map((s) => `[${s.n}] ${s.url}`)
    ]),
    '',
    p.askAiNote
  ].join('\n')

  return (
    <div className="ask-answers">
      {result.answers.map((answer, index) => (
        <article key={index} className={`ask-answer basis-${answer.basis}`}>
          <p className="ask-basis">
            {answer.basis === 'document' ? <FileText size={15} aria-hidden="true" /> : <Globe size={15} aria-hidden="true" />}
            {answer.basis === 'document' ? p.askFromDoc : p.askFromWeb}
          </p>
          <p className="ask-text" lang={languageInfo(language).bcp47}>
            <Cited text={answer.text} sources={answer.sources} />
          </p>
          {answer.quotes.map((quote, i) => (
            <blockquote key={i} className="evidence">
              <span className="evidence-label">{p.docSays}</span>
              <span className="evidence-text">“{quote}”</span>
            </blockquote>
          ))}
          {answer.basis === 'document' && !answer.verified && (
            <p className="unclear-note">
              <CircleAlert size={15} aria-hidden="true" /> {p.askUnverified}
            </p>
          )}
          {answer.sources.length > 0 && (
            <div className="ask-sources">
              <span className="ask-sources-label">{p.askSources}</span>
              <ul>
                {answer.sources.map((source) => (
                  <li key={source.n} id={`src-${source.n}`}>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      <span className="ask-source-n">{source.n}</span>
                      <span className="ask-source-body">
                        <span className="ask-source-host">
                          <BadgeCheck size={14} aria-hidden="true" /> {displayHost(source.url)} · {p.askOfficial}
                        </span>
                        <span className="ask-source-title">{source.title}</span>
                      </span>
                      <ExternalLink size={16} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>
      ))}
      <div className="ask-actions">
        {speech.supported && speech.hasVoice(language) && (
          <button className="inline-action" onClick={() => speech.play(spoken, language)}>
            <Volume2 size={16} aria-hidden="true" /> {p.listen}
          </button>
        )}
        <button className="inline-action" onClick={() => onShare(shareText)}>
          <Share2 size={16} aria-hidden="true" /> {p.share}
        </button>
      </div>
    </div>
  )
}

/** Answer text with [1]-style citations turned into small numbered badges. */
function Cited({ text, sources }: { text: string; sources: AskResult['answers'][number]['sources'] }) {
  const parts = text.split(/(\[\d+(?:\s*,\s*\d+)*\])/g)
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^\[(.+)\]$/)
        if (!match) return <Fragment key={i}>{part}</Fragment>
        const numbers = match[1].split(',').map((n) => Number(n.trim()))
        return numbers.map((n) => {
          const source = sources.find((s) => s.n === n)
          return source ? (
            <a key={`${i}-${n}`} className="cite" href={source.url} target="_blank" rel="noopener noreferrer" title={displayHost(source.url)}>
              {n}
            </a>
          ) : null
        })
      })}
    </>
  )
}
